import { GpsGeoidPb } from "@skydio/pbtypes/pbtypes/gen/sensors/gps_geoid_pb";
import { FrameType_Enum } from "@skydio/pbtypes/pbtypes/vehicle/skills/waypoints";

import { VEHICLE_SPEED_LIMITS, WAYPOINT_SPEED, RETURN_SPEED } from "@skydio/mission-logic";

import { isX10 } from "@skydio/mission-logic";
import mapCaptureSettingsJson from "./map_capture_settings.json";

import type { PositionZ } from "@skydio/pbtypes/pbtypes/vehicle/skills/waypoints";
import type { VehicleConfiguration } from "./state/vehicleConfiguration";
import { useFeatureFlag } from "hooks/useFeatureFlag";

/**
 * Get waypoint speed constraints based on vehicle configuration and flight mode.
 *
 * @remarks
 * Wraps the shared mission-logic constraints with customer_portal-specific
 * dfrStockSettings override that allows MAX_TRANSLATIONAL speed.
 */
export const getWaypointSpeed = (
  twilightMode = false,
  vehicleConfiguration?: VehicleConfiguration,
  dfrStockSettingsEnabled = false
) => {
  const flightMode = twilightMode ? "twilight" : "nominal";
  // If we don't have a vehicle configuration, default to r47 max speed
  const isR47OrX10 = !vehicleConfiguration || isX10(vehicleConfiguration);
  const maxSpeedObject = isR47OrX10 ? WAYPOINT_SPEED.MAX.R47_X10 : WAYPOINT_SPEED.MAX.S2_X2;
  const MAX_SPEED = maxSpeedObject[flightMode];
  return {
    MIN: WAYPOINT_SPEED.MIN,
    MAX: dfrStockSettingsEnabled ? VEHICLE_SPEED_LIMITS.MAX_TRANSLATIONAL : MAX_SPEED,
    DEFAULT: WAYPOINT_SPEED.DEFAULT,
    DEFAULT_INDOOR: WAYPOINT_SPEED.DEFAULT_INDOOR,
  };
};

/**
 * returns waypoint speed settings based on fastFlight feature flags and whether we are in
 * twilightMode
 */
export function useWaypointSpeedSettings(
  twilightMode: boolean,
  vehicleConfiguration?: VehicleConfiguration
) {
  const dfrStockSettingsEnabled = useFeatureFlag("dfrStockSettings");
  // get waypoint speed settings based on whether we are in twilightMode
  return getWaypointSpeed(twilightMode, vehicleConfiguration, dfrStockSettingsEnabled);
}

/**
 * Get return speed constraints based on vehicle configuration and flight mode.
 *
 * @remarks
 * See vehicle/ambassador/params.py : return_settings_limits for detailed information
 */
export const getReturnSpeed = (
  twilightMode = false,
  vehicleConfiguration?: VehicleConfiguration
) => {
  const isR47Series = isX10(vehicleConfiguration);
  const flightMode = twilightMode ? "twilight" : "nominal";

  const defaultReturnSpeed = isR47Series ? RETURN_SPEED.DEFAULT_R47 : RETURN_SPEED.DEFAULT;

  const maxSpeedObject = isR47Series ? RETURN_SPEED.MAX.R47_X10 : RETURN_SPEED.MAX.S2_X2;
  const maxReturnSpeed = maxSpeedObject[flightMode];

  return {
    MIN: RETURN_SPEED.MIN,
    MAX: maxReturnSpeed,
    DEFAULT: defaultReturnSpeed,
  };
};

/**
 * returns return speed settings based on whether we are in twilightMode
 */
export function useReturnSpeedSettings(
  twilightMode: boolean,
  vehicleConfiguration?: VehicleConfiguration
) {
  // get waypoint speed settings based on fastFlightEnabled and whether we are in twilightMode
  return getReturnSpeed(twilightMode, vehicleConfiguration);
}

/**
 * Additional time in seconds to add to the scan plan time estimate to get the mission time estimate.
 * This accounts for takeoff, going to the scan area, returning and landing.
 *
 * @remarks
 * Used primarily for map capture (SURFACE_SCAN) missions where frontend calculates
 * total mission duration as: scanPlanTime + BUFFER_TIME_FOR_MISSION_SEC
 */
export const BUFFER_TIME_FOR_MISSION_SEC = 150;

/**
 * These mission types have GPS points in them
 */
export const GPS_MISSION_TYPES = new Set<string | undefined>(["GPS", "MIXED"]);

/**
 * Frames that can be interpreted as takeoff relative and are used as such.
 */
const TAKEOFF_RELATIVE_FRAMES = [
  // Moving forward, this should be the takeoff relative frame of choice (for outdoor missions)
  FrameType_Enum.WORLD_TAKEOFF,
  FrameType_Enum.NAV_TAKEOFF,
  FrameType_Enum.NAV,
];

export function isTakeoffRelativeFrame(frame: FrameType_Enum): boolean {
  return TAKEOFF_RELATIVE_FRAMES.includes(frame);
}

export function isPositionZEgm96Altitude(z: PositionZ): boolean {
  if (z?.frame !== FrameType_Enum.GPS) {
    return false;
  }
  if (
    // Unspecified is interpreted as EGM96 for backwards compatibility.
    (z?.frameDetails?.zReference || GpsGeoidPb.Enum.UNKNOWN) == GpsGeoidPb.Enum.UNKNOWN ||
    z?.frameDetails?.zReference == GpsGeoidPb.Enum.EGM96
  ) {
    return true;
  }
  return false;
}

export function humanizeGpsZFrameReference(positionZ: PositionZ | undefined): string {
  switch (
    (positionZ?.frameDetails?.zReference ?? undefined) as
      | GpsGeoidPb.EnumMap[keyof GpsGeoidPb.EnumMap]
      | undefined
  ) {
    case undefined:
    case GpsGeoidPb.Enum.UNKNOWN:
    case GpsGeoidPb.Enum.EGM96:
      return "MSL (EGM96)";
    case GpsGeoidPb.Enum.EGM08:
      return "MSL (EGM08)";
    case GpsGeoidPb.Enum.WGS84:
      return "Ellipsoid (WGS84)";
  }
}

/**
 * Settings keys for surface scan action initialization. Any settings in the SurfaceScanActionArgs
 * which are not in this list will be ignored by the action.
 *
 * @remarks
 * Loaded from map_capture_settings.json which is also read by Python tests for cross-language
 * validation.
 * Python counterpart: util/surface_scan/surface_scan_mission_util.py::ALLOWED_SETTINGS_KEYS_FOR_MAP_CAPTURE
 */
export const SURFACE_SCAN_SETTINGS_KEYS =
  mapCaptureSettingsJson.surfaceScanSettingsKeys as readonly string[];
